import { useEffect, useState, FormEvent } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { adminApi, AdminClient, AdminModule, AdminClientUser, AdminStaffUser, AuditEvent, ClientAdminRow, ClientClientverseState, ClientStagingState, ClientverseVersion, EntraGroup } from '../api/admin';
import { useAdminCtx } from './AdminShell';
import { moduleOptions, parseScope, scopeSummary, toggleScope } from './moduleAccess';
import ClientDataMapping from './ClientDataMapping';
import ClientStagingCard from './ClientStagingCard';

export default function AdminClientDetail() {
  const { clientId } = useParams();
  const [searchParams] = useSearchParams();
  const slug = searchParams.get('slug');
  const { getToken } = useAuth();
  const { staffRole } = useAdminCtx();
  const isAdmin = staffRole === 'admin';
  const [client, setClient] = useState<AdminClient | null>(null);
  const [tab, setTab] = useState<'modules' | 'users' | 'admins' | 'signin' | 'datamapping' | 'activity'>('modules');
  const [modules, setModules] = useState<AdminModule[] | null>(null);
  const [users, setUsers] = useState<AdminClientUser[] | null>(null);
  const [clientAdmins, setClientAdmins] = useState<ClientAdminRow[] | null>(null);
  const [eligibleStaff, setEligibleStaff] = useState<AdminStaffUser[] | null>(null);
  const [cvState, setCvState] = useState<ClientClientverseState | null>(null);
  const [cvVersions, setCvVersions] = useState<ClientverseVersion[] | null>(null);
  const [cvBusy, setCvBusy] = useState(false);
  const [stgState, setStgState] = useState<ClientStagingState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const t = await getToken();
      const [c, m, u, cv, cvVers, stg] = await Promise.all([
        adminApi.getClient(t, clientId!),
        adminApi.listModules(t, clientId!),
        adminApi.listClientUsers(t, clientId!),
        adminApi.getClientClientverse(t, clientId!),
        adminApi.listClientverseVersions(t),
        adminApi.getClientStaging(t, clientId!),
      ]);
      setClient(c);
      setModules(m);
      setUsers(u);
      setCvState(cv);
      setCvVersions(cvVers);
      setStgState(stg);
      if (isAdmin) {
        const [ca, all] = await Promise.all([
          adminApi.listClientAdmins(t, clientId!),
          adminApi.listStaffUsers(t),
        ]);
        setClientAdmins(ca);
        setEligibleStaff(all.filter((x) => x.role === 'client_admin'));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const setClientversePin = async (versionId: number | null) => {
    if (!isAdmin) return;
    setCvBusy(true);
    try {
      await adminApi.setClientClientversePin(await getToken(), clientId!, versionId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCvBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [clientId, isAdmin]);

  const addClientAdmin = async (userId: string) => {
    try {
      await adminApi.addClientAdmin(await getToken(), clientId!, userId);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };
  const removeClientAdmin = async (userId: string) => {
    try {
      await adminApi.removeClientAdmin(await getToken(), clientId!, userId);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const linkGroup = async (groupId: string | null) => {
    try {
      const r = await adminApi.linkEntraGroup(await getToken(), clientId!, groupId);
      if (r.sync) alert(`Linked. Synced ${r.sync.members} member(s): +${r.sync.added} / −${r.sync.removed}.`);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };
  const syncGroup = async () => {
    try {
      const r = await adminApi.syncEntraGroup(await getToken(), clientId!);
      const extra = r.raw_members !== undefined
        ? `\n\nTeam returned ${r.raw_members} member(s); ${r.skipped_non_staff ?? 0} skipped (not @mountinsights.com).`
        : '';
      alert(`Sync complete. ${r.members} staff member(s) linked: +${r.added} / −${r.removed}.${extra}`);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const toggleUserDisabled = async (u: AdminClientUser) => {
    if (!u.id) return;
    try {
      const t = await getToken();
      await adminApi.setUserDisabled(t, clientId!, u.id, !u.disabled_at);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const setUserModules = async (u: AdminClientUser, scope: string[] | null) => {
    if (!u.id) return;
    try {
      await adminApi.setUserModules(await getToken(), clientId!, u.id, scope);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const setDefaultScope = async (scope: string[] | null) => {
    try {
      await adminApi.setDefaultModuleScope(await getToken(), clientId!, scope);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const toggle = async (m: AdminModule) => {
    try {
      const t = await getToken();
      await adminApi.toggleModule(t, clientId!, m.module_id, !m.enabled);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // Independent of `enabled`, so preview can be set before a module is switched on and the client
  // never sees it in between.
  const togglePreview = async (m: AdminModule) => {
    try {
      const t = await getToken();
      await adminApi.toggleModule(t, clientId!, m.module_id, m.enabled, !m.staff_only);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div>
      <Link to="/admin/clients" style={{ fontSize: 14 }}>← Back to clients</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ color: 'var(--mi-navy)', marginBottom: 4 }}>{client?.name ?? '…'}</h1>
        {slug && (
          <a href={`/${slug}`} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ padding: '8px 12px', borderRadius: 6, textDecoration: 'none', color: 'white' }}>
            Open portal ↗
          </a>
        )}
      </div>
      {client && <div style={{ fontSize: 13, color: 'var(--mi-muted)', marginBottom: 16 }}><code>{client.slug}</code></div>}
      {error && <div style={{ background: '#fee', padding: 12, borderRadius: 6, marginBottom: 12 }}>{error}</div>}

      <nav style={tabBar}>
        <button onClick={() => setTab('modules')} style={tab === 'modules' ? tabActive : tabBtn}>Modules</button>
        <button onClick={() => setTab('users')} style={tab === 'users' ? tabActive : tabBtn}>Users</button>
        {isAdmin && <button onClick={() => setTab('admins')} style={tab === 'admins' ? tabActive : tabBtn}>Client Admins</button>}
        {isAdmin && <button onClick={() => setTab('signin')} style={tab === 'signin' ? tabActive : tabBtn}>Sign-in</button>}
        {isAdmin && <button onClick={() => setTab('datamapping')} style={tab === 'datamapping' ? tabActive : tabBtn}>Data Mapping</button>}
        <button onClick={() => setTab('activity')} style={tab === 'activity' ? tabActive : tabBtn}>Activity</button>
      </nav>

      {tab === 'modules' && (
      <section style={{ marginBottom: 24 }}>
        {!modules ? (
          <div>Loading…</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {modules.map((m) => (
              <div key={m.module_id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', flex: 1 }}>
                  <input type="checkbox" checked={m.enabled} onChange={() => toggle(m)} />
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {m.name}
                      {m.enabled && m.staff_only && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#9a4a12', background: '#fdeee2', borderRadius: 3, padding: '1px 6px' }}>Staff preview</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--mi-muted)' }}><code>{m.code}</code></div>
                  </div>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--mi-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  title="Visible to Mount Insights staff only; client users neither see it nor reach its API. Tick this before enabling a module to keep clients out from the start.">
                  <input type="checkbox" checked={!!m.staff_only} onChange={() => togglePreview(m)} aria-label={`Staff preview only: ${m.name}`} />
                  Staff preview only
                </label>
                {(m.code === 'asana-board' || m.code === 'ui-signoff' || m.code === 'uat' || m.code === 'pr-review') && (
                  <Link
                    to={`/admin/clients/${clientId}/${m.code}`}
                    style={{ color: 'var(--mi-purple)', textDecoration: 'none', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}
                  >
                    Configure →
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ClientVerse Version — drives what schema the Data Mapping module shows. */}
        {cvState && (
          <div className="card" style={{ marginTop: 16 }}>
            <h3 style={{ margin: 0, fontSize: 14, color: 'var(--mi-navy)' }}>ClientVerse Version</h3>
            <div style={{ fontSize: 12, color: 'var(--mi-muted)', marginTop: 4 }}>
              Controls which version of the Salesforce ClientVerse package this client sees in the Data Mapping module. Leave unpinned to follow the latest synced version.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8, marginTop: 12, fontSize: 13, alignItems: 'baseline' }}>
              <div style={{ color: 'var(--mi-muted)' }}>Pinned to:</div>
              <div style={{ fontWeight: 600 }}>
                {cvState.pinned_version_string ?? <span style={{ color: 'var(--mi-muted)', fontWeight: 400 }}>not pinned — follows latest</span>}
              </div>
              <div style={{ color: 'var(--mi-muted)' }}>Latest synced:</div>
              <div>
                {cvState.latest_version_string ?? <span style={{ color: 'var(--mi-muted)' }}>no version observed yet</span>}
              </div>
            </div>

            {isAdmin && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                <select
                  value={cvState.pinned_version_id ?? ''}
                  disabled={cvBusy || !cvVersions || cvVersions.length === 0}
                  onChange={(e) => {
                    const v = e.target.value;
                    setClientversePin(v === '' ? null : Number(v));
                  }}
                  style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--mi-border)' }}
                >
                  <option value="">(unpinned — latest)</option>
                  {(cvVersions ?? []).map((v) => (
                    <option key={v.id} value={v.id}>{v.version_string}</option>
                  ))}
                </select>

                {cvState.pinned_version_id !== null
                  && cvState.latest_version_id !== null
                  && cvState.pinned_version_id !== cvState.latest_version_id && (
                  <button
                    onClick={() => setClientversePin(cvState.latest_version_id)}
                    disabled={cvBusy}
                    className="btn-primary"
                    style={{ padding: '4px 10px', fontSize: 12 }}
                  >
                    Upgrade to latest ({cvState.latest_version_string})
                  </button>
                )}
                {cvState.pinned_version_id !== null && (
                  <button
                    onClick={() => setClientversePin(null)}
                    disabled={cvBusy}
                    style={{ background: 'transparent', border: '1px solid var(--mi-border)', padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: 'var(--mi-muted)' }}
                  >
                    Clear pin
                  </button>
                )}
                {cvBusy && <span style={{ fontSize: 12, color: 'var(--mi-muted)' }}>saving…</span>}
              </div>
            )}
            {!isAdmin && cvState.pinned_version_id === null && (
              <div style={{ fontSize: 12, color: 'var(--mi-muted)', marginTop: 8 }}>
                Only Mount Insights admins can pin or upgrade the ClientVerse version.
              </div>
            )}
          </div>
        )}

        {stgState && (
          <ClientStagingCard clientId={clientId!} isAdmin={isAdmin} state={stgState} onChanged={load} />
        )}
      </section>
      )}

      {tab === 'users' && (
      <section style={{ marginBottom: 24 }}>
        <h2 style={{ color: 'var(--mi-navy)', fontSize: 18 }}>Users</h2>
        {!users ? (
          <div>Loading…</div>
        ) : (
          <>
            {users.length === 0 ? (
              <div className="card" style={{ marginBottom: 16 }}>No users yet. Add one below.</div>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Title</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Last login</th>
                    <th style={thStyle}>Sees</th>
                    <th style={thStyle}>Invite URL</th>
                    <th style={thStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={`${u.status}-${u.id ?? u.email}`}>
                      <td style={tdStyle}>{u.email}</td>
                      <td style={tdStyle}>{u.display_name ?? '—'}</td>
                      <td style={tdStyle}>{u.title ?? '—'}</td>
                      <td style={tdStyle}>{statusBadge(u)}</td>
                      <td style={tdStyle}>
                        {u.last_login_at ? (
                          <span title={new Date(u.last_login_at).toISOString()}>
                            {new Date(u.last_login_at).toLocaleString()}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--mi-muted)' }}>
                            {u.status === 'invited' ? '—' : 'never'}
                          </span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {u.status === 'active' && u.id
                          ? <ModuleAccess
                              label={u.email}
                              scope={parseScope(u.module_scope)}
                              modules={modules ?? []}
                              onChange={(scope) => setUserModules(u, scope)}
                            />
                          : <span style={{ color: 'var(--mi-muted)' }}>
                              {scopeSummary(parseScope(u.module_scope), moduleOptions(modules ?? [], parseScope(u.module_scope)))}
                            </span>}
                      </td>
                      <td style={tdStyle}>
                        {u.status === 'invited'
                          ? <InviteUrlCell token={u.invite_token} />
                          : <span style={{ color: 'var(--mi-muted)' }}>—</span>}
                      </td>
                      <td style={tdStyle}>
                        {u.status === 'active' && u.id && (
                          <button
                            onClick={() => toggleUserDisabled(u)}
                            style={{ background: 'transparent', color: u.disabled_at ? 'var(--mi-green)' : '#900', border: '1px solid var(--mi-border)' }}
                          >
                            {u.disabled_at ? 'Enable' : 'Disable'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="card" style={{ marginTop: 16 }}>
              <h3 style={{ marginTop: 0, fontSize: 15, color: 'var(--mi-navy)' }}>New users start with</h3>
              <ModuleAccess label="new users" scope={parseScope(client?.default_module_scope)} modules={modules ?? []} onChange={setDefaultScope} />
              <p style={{ fontSize: 12, color: 'var(--mi-muted)', marginTop: 8, marginBottom: 0 }}>
                Copied onto each user when they are invited or first sign in through this client's SSO. It never changes anyone who is already here; change a user in the Sees column.
              </p>
            </div>
            <CreateClientUserForm key={client?.default_module_scope ?? 'all'} clientId={clientId!} modules={modules ?? []} defaultScope={parseScope(client?.default_module_scope)} onCreated={load} getToken={getToken} setError={setError} />
          </>
        )}
      </section>
      )}

      {tab === 'admins' && isAdmin && (() => {
        const admins = (clientAdmins ?? []).filter((u) => u.role === 'admin');
        const assigned = (clientAdmins ?? []).filter((u) => u.role === 'client_admin');
        return (
        <section style={{ marginBottom: 24 }}>
          <h2 style={{ color: 'var(--mi-navy)', fontSize: 18 }}>Who can manage this client</h2>
          <p style={{ fontSize: 13, color: 'var(--mi-muted)', marginTop: 0 }}>
            Manage users in <Link to="/admin/users">Users</Link>.
          </p>

          <EntraTeamLink
            client={client}
            getToken={getToken}
            onLink={linkGroup}
            onSync={syncGroup}
          />

          {clientAdmins === null ? (
            <div>Loading…</div>
          ) : (
            <>
              <h3 style={subHead}>Admins <span style={countPill}>{admins.length}</span></h3>
              <p style={sectionHint}>These users have access to every client.</p>
              {admins.length === 0 ? (
                <div className="card" style={{ marginBottom: 16 }}>No admins.</div>
              ) : (
                <table style={tableStyle}>
                  <thead>
                    <tr><th style={thStyle}>Email</th><th style={thStyle}>Name</th></tr>
                  </thead>
                  <tbody>
                    {admins.map((u) => (
                      <tr key={u.id}>
                        <td style={tdStyle}>{u.email}</td>
                        <td style={tdStyle}>{u.display_name ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h3 style={{ ...subHead, marginTop: 24 }}>Client Admins <span style={countPill}>{assigned.length}</span></h3>
              <p style={sectionHint}>Assigned explicitly to this client.</p>
              {assigned.length === 0 ? (
                <div className="card" style={{ marginBottom: 8 }}>No client admins assigned yet.</div>
              ) : (
                <table style={tableStyle}>
                  <thead>
                    <tr><th style={thStyle}>Email</th><th style={thStyle}>Name</th><th style={thStyle}>Source</th><th style={thStyle}></th></tr>
                  </thead>
                  <tbody>
                    {assigned.map((u) => (
                      <tr key={u.id}>
                        <td style={tdStyle}>{u.email}</td>
                        <td style={tdStyle}>{u.display_name ?? '—'}</td>
                        <td style={tdStyle}>
                          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {u.via_group && <span style={teamBadge}>via Team</span>}
                            {u.manual && <span style={manualBadge}>Manually added</span>}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          {u.manual ? (
                            <button
                              onClick={() => {
                                const msg = u.via_group
                                  ? `Remove the manual grant for ${u.email}? They'll keep access via the Team until removed there.`
                                  : `Remove ${u.email} from this client?`;
                                if (confirm(msg)) removeClientAdmin(u.id);
                              }}
                              style={{ background: 'transparent', color: '#900', border: '1px solid var(--mi-border)' }}
                            >Remove</button>
                          ) : (
                            <span style={{ fontSize: 12, color: 'var(--mi-muted)' }} title="Managed by the linked Team — remove this person from the Team to revoke.">Team-managed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
          {eligibleStaff && (() => {
            // Exclude anyone who already has access — assigned client_admins
            // AND the global admins. Lowercase ids to avoid UUID case drift.
            const takenIds = new Set((clientAdmins ?? []).map((c) => c.id.toLowerCase()));
            const available = eligibleStaff.filter((u) => !takenIds.has(u.id.toLowerCase()));
            return (
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--mi-muted)' }}>Assign Client Admin:</span>
                {available.length === 0 ? (
                  <span style={{ fontSize: 13, color: 'var(--mi-muted)' }}>No unassigned Client Admin users.</span>
                ) : available.map((u) => (
                  <button key={u.id} onClick={() => addClientAdmin(u.id)} style={{ padding: '4px 10px', borderRadius: 999, border: '1px solid var(--mi-border)', background: '#eef2ff', cursor: 'pointer', fontSize: 13 }}>
                    + {u.email}
                  </button>
                ))}
              </div>
            );
          })()}
        </section>
        );
      })()}

      {tab === 'datamapping' && isAdmin && (
        <section style={{ marginBottom: 24 }}>
          <h2 style={{ color: 'var(--mi-navy)', fontSize: 18 }}>Data Mapping</h2>
          <ClientDataMapping clientId={clientId!} />
        </section>
      )}

      {tab === 'signin' && isAdmin && <SignInTab client={client} getToken={getToken} onSaved={load} setError={setError} />}

      {tab === 'activity' && <ActivityTab clientId={clientId!} getToken={getToken} />}
    </div>
  );
}

// Links a client to a Microsoft 365 Team (Entra group) and triggers sync.
function EntraTeamLink({ client, getToken, onLink, onSync }: {
  client: AdminClient | null;
  getToken: () => Promise<string | null>;
  onLink: (groupId: string | null) => Promise<void>;
  onSync: () => Promise<void>;
}) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<EntraGroup[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const search = async () => {
    if (q.trim().length < 2) return;
    setSearching(true);
    try {
      setResults(await adminApi.searchEntraGroups(await getToken(), q.trim()));
    } catch (e) {
      if (e instanceof Error && /graph_not_configured|503/.test(e.message)) setUnavailable(true);
      setResults([]);
    } finally { setSearching(false); }
  };

  const wrap = (fn: () => Promise<void>) => async () => { setBusy(true); try { await fn(); } finally { setBusy(false); } };

  if (client?.entra_group_id) {
    return (
      <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--mi-muted)' }}>Linked Team</div>
          <div style={{ fontWeight: 600 }}>{client.entra_group_name ?? client.entra_group_id}</div>
          <div style={{ fontSize: 12, color: 'var(--mi-muted)' }}>
            {client.entra_synced_at ? `Synced ${new Date(client.entra_synced_at).toLocaleString()}` : 'Not synced yet'}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={wrap(onSync)} disabled={busy} className="btn-primary" style={{ padding: '6px 12px', borderRadius: 6 }}>{busy ? 'Working…' : 'Sync now'}</button>
          <button onClick={wrap(() => onLink(null))} disabled={busy} style={{ background: 'transparent', color: '#900', border: '1px solid var(--mi-border)', borderRadius: 6, padding: '6px 12px' }}>Unlink</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, color: 'var(--mi-muted)', marginBottom: 8 }}>
        Link a Microsoft 365 Team so its members automatically get Client Admin access.
      </div>
      {unavailable ? (
        <div style={{ fontSize: 13, color: 'var(--mi-muted)' }}>Microsoft Graph isn’t configured on the server — group sync is unavailable.</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              placeholder="Search Teams by name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
              style={{ flex: 1, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--mi-border)' }}
            />
            <button onClick={search} disabled={searching || q.trim().length < 2} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--mi-border)', background: '#fff' }}>{searching ? 'Searching…' : 'Search'}</button>
          </div>
          {results && results.length === 0 && <div style={{ fontSize: 13, color: 'var(--mi-muted)', marginTop: 8 }}>No matching Teams.</div>}
          {results && results.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {results.map((g) => (
                <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: 1 }}>{g.displayName}</span>
                  <button onClick={wrap(() => onLink(g.id))} disabled={busy} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--mi-border)', background: '#eef2ff' }}>Link</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SignInTab({ client, getToken, onSaved, setError }: {
  client: AdminClient | null;
  getToken: () => Promise<string | null>;
  onSaved: () => Promise<void>;
  setError: (msg: string) => void;
}) {
  const [method, setMethod] = useState<'password' | 'sso' | 'either'>('password');
  const [tenant, setTenant] = useState('');
  const [group, setGroup] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [domains, setDomains] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Hydrate from the loaded client. Re-hydrate when the underlying client
  // changes (e.g. after a save → reload).
  useEffect(() => {
    setMethod(client?.signin_method ?? 'password');
    setTenant(client?.external_tenant_id ?? '');
    setGroup(client?.external_group_id ?? '');
    setDisplayName(client?.external_display_name ?? '');
    setDomains(client?.external_email_domains ?? '');
  }, [client?.id, client?.signin_method, client?.external_tenant_id, client?.external_group_id, client?.external_display_name, client?.external_email_domains]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!client) return;
    setBusy(true);
    try {
      await adminApi.updateClientSso(await getToken(), client.id, {
        external_tenant_id: tenant.trim() || null,
        external_group_id: group.trim() || null,
        external_display_name: displayName.trim() || null,
        external_email_domains: domains.trim() || null,
        signin_method: method,
      });
      setSavedAt(Date.now());
      await onSaved();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const ssoReady = !!tenant.trim() && !!group.trim();
  const deepLinkUrl = client ? `${window.location.origin}/login/${client.slug}` : '';

  return (
    <section style={{ marginBottom: 24, maxWidth: 720 }}>
      <h2 style={{ color: 'var(--mi-navy)', fontSize: 18 }}>Sign-in</h2>
      <p style={{ fontSize: 13, color: 'var(--mi-muted)', marginTop: 0 }}>
        Configure how this client&rsquo;s users authenticate. Password sign-in always works for users you add on the Users tab; SSO routes users through Microsoft 365 using the client&rsquo;s own Entra tenant.
      </p>

      <form onSubmit={save} className="card" style={{ display: 'grid', gap: 12 }}>
        <label style={field}>
          <div style={fieldLabel}>Sign-in method</div>
          <select value={method} onChange={(e) => setMethod(e.target.value as 'password' | 'sso' | 'either')} style={input}>
            <option value="password">Password only (current default)</option>
            <option value="sso" disabled={!ssoReady}>SSO only{!ssoReady ? ' — set tenant + group below first' : ''}</option>
            <option value="either" disabled={!ssoReady}>Either (SSO with password fallback)</option>
          </select>
        </label>

        <label style={field}>
          <div style={fieldLabel}>Microsoft tenant ID (GUID)</div>
          <input value={tenant} onChange={(e) => setTenant(e.target.value)} placeholder="e.g. 11111111-2222-3333-4444-555555555555" style={input} />
        </label>

        <label style={field}>
          <div style={fieldLabel}>Access group ID (GUID)</div>
          <input value={group} onChange={(e) => setGroup(e.target.value)} placeholder="Object ID of the security group / Team controlling access" style={input} />
        </label>

        <label style={field}>
          <div style={fieldLabel}>Sign-in button label <span style={{ color: 'var(--mi-muted)', fontWeight: 400 }}>(optional)</span></div>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={`e.g. Sign in with ${client?.name ?? 'Acme'}`} style={input} />
        </label>

        <label style={field}>
          <div style={fieldLabel}>Allowed email domains <span style={{ color: 'var(--mi-muted)', fontWeight: 400 }}>(optional, comma-separated)</span></div>
          <input value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="acme.com, acme.co.uk" style={input} />
          <div style={{ fontSize: 12, color: 'var(--mi-muted)', marginTop: 4 }}>
            Used so a user typing their email on the login page is routed to this client&rsquo;s SSO automatically.
          </div>
        </label>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          {savedAt && <span style={{ fontSize: 12, color: 'var(--mi-muted)' }}>Saved.</span>}
        </div>
      </form>

      {client && (
        <div className="card" style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, color: 'var(--mi-muted)' }}>Direct sign-in link for this client</div>
          <code style={{ wordBreak: 'break-all' }}>{deepLinkUrl}</code>
          <div style={{ fontSize: 12, color: 'var(--mi-muted)', marginTop: 6 }}>
            Share this with the client. When SSO is enabled, opening this link takes their users straight to Microsoft sign-in.
          </div>
        </div>
      )}
    </section>
  );
}

const field: React.CSSProperties = { display: 'block' };
const fieldLabel: React.CSSProperties = { fontSize: 13, color: 'var(--mi-navy)', fontWeight: 600, marginBottom: 4 };
const input: React.CSSProperties = { width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--mi-border)' };

const teamBadge: React.CSSProperties = { padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: '#eef2ff', color: '#3b3b88' };
const manualBadge: React.CSSProperties = { padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: '#f1f3f5', color: '#555' };

const MODULE_LABEL: Record<string, string> = {
  'qa-tracker': 'Q&A Tracker',
  'action-tracker': 'Action Tracker',
};
function ActivityTab({ clientId, getToken }: { clientId: string; getToken: () => Promise<string | null> }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ items: AuditEvent[]; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 50;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await adminApi.clientAudit(await getToken(), clientId, page, PAGE_SIZE);
        setData({ items: r.items, total: r.total });
      } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
      finally { setLoading(false); }
    })();
  }, [clientId, getToken, page]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <section style={{ marginBottom: 24 }}>
      <h2 style={{ color: 'var(--mi-navy)', fontSize: 18 }}>Activity</h2>
      {error && <div style={{ background: '#fee', color: '#900', padding: 12, borderRadius: 6, marginBottom: 12 }}>{error}</div>}
      {!data ? <div>Loading…</div> : (
        <>
          <div style={{ marginBottom: 8, color: 'var(--mi-muted)', fontSize: 13 }}>
            {data.total.toLocaleString()} event{data.total === 1 ? '' : 's'}
          </div>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>When</th>
                <th style={thStyle}>Module</th>
                <th style={thStyle}>Item</th>
                <th style={thStyle}>Change</th>
                <th style={thStyle}>User</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((ev) => (
                <tr key={ev.id} style={{ borderTop: '1px solid var(--mi-border)' }}>
                  <td style={tdStyle}>{new Date(ev.created_at).toLocaleString()}</td>
                  <td style={tdStyle}>{MODULE_LABEL[ev.module] ?? ev.module}</td>
                  <td style={{ ...tdStyle, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ev.item_label ?? ''}>
                    {ev.item_label ?? '—'}
                  </td>
                  <td style={tdStyle}>{ev.action_label}</td>
                  <td style={tdStyle}>{ev.user_name ?? ev.user_email ?? '—'}</td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td style={tdStyle} colSpan={5}>No activity yet.</td></tr>}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} style={{ ...inputBase, cursor: 'pointer' }}>← Prev</button>
            <div style={{ fontSize: 13, color: 'var(--mi-muted)' }}>Page {page} of {totalPages}</div>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading} style={{ ...inputBase, cursor: 'pointer' }}>Next →</button>
          </div>
        </>
      )}
    </section>
  );
}

function statusBadge(u: AdminClientUser) {
  if (u.status === 'invited') return <span style={{ color: 'var(--mi-gold)' }}>Invited</span>;
  if (u.disabled_at) return <span style={{ color: '#900' }}>Disabled</span>;
  return <span style={{ color: 'var(--mi-green)' }}>Active</span>;
}

function InviteUrlCell({ token }: { token: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!token) return <span style={{ color: 'var(--mi-muted)', fontSize: 13 }}>—</span>;
  const url = `${window.location.origin}/accept-invite?token=${token}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* ignore */ }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input readOnly value={url} onFocus={(e) => e.currentTarget.select()} style={{ flex: 1, fontSize: 12, padding: '4px 6px', border: '1px solid var(--mi-border)', borderRadius: 6, fontFamily: 'monospace' }} />
      <button onClick={copy} className="btn-primary" style={{ padding: '4px 10px', fontSize: 12 }}>{copied ? 'Copied' : 'Copy'}</button>
    </div>
  );
}

// Which modules a client user can reach. Ticking "Everything enabled" lifts the limit, including for
// modules switched on later; unticking it lists the client's enabled modules to choose from.
function ModuleAccess({ label, scope, modules, onChange }: {
  label: string;
  scope: string[] | null;
  modules: AdminModule[];
  onChange: (scope: string[] | null) => void;
}) {
  const options = moduleOptions(modules, scope);
  return (
    <details>
      <summary style={{ cursor: 'pointer' }}>{scopeSummary(scope, options)}</summary>
      <div role="group" aria-label={`Modules ${label} can see`} style={{ display: 'grid', gap: 4, marginTop: 6, fontSize: 13 }}>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={scope === null}
            disabled={scope === null && options.length === 0}
            onChange={(e) => onChange(e.target.checked ? null : options.map((o) => o.code))}
          />
          Everything enabled
        </label>
        {scope !== null && options.map((o) => (
          <label key={o.code} style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 16 }}>
            <input
              type="checkbox"
              checked={scope.includes(o.code)}
              disabled={scope.length === 1 && scope[0] === o.code}
              onChange={() => { const next = toggleScope(scope, o.code); if (next) onChange(next); }}
            />
            {o.name}
          </label>
        ))}
      </div>
    </details>
  );
}

function CreateClientUserForm({ clientId, modules, defaultScope, onCreated, getToken, setError }: {
  clientId: string;
  modules: AdminModule[];
  defaultScope: string[] | null;
  onCreated: () => Promise<void>;
  getToken: () => Promise<string | null>;
  setError: (msg: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [scope, setScope] = useState<string[] | null>(defaultScope);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !name.trim() || busy) return;
    setBusy(true);
    try {
      await adminApi.createClientUser(await getToken(), clientId, {
        email: email.trim(),
        display_name: name.trim(),
        title: title.trim() || undefined,
        moduleScope: scope,
      });
      setEmail(''); setName(''); setTitle(''); setScope(defaultScope);
      await onCreated();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="card" style={{ marginTop: 16 }}>
      <h3 style={{ marginTop: 0, fontSize: 15, color: 'var(--mi-navy)' }}>Add a client user</h3>
      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr 1fr auto' }}>
        <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} style={inputBase} />
        <input required placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} style={inputBase} />
        <input placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} style={inputBase} />
        <button type="submit" className="btn-primary" disabled={busy || !email.trim() || !name.trim()}>{busy ? 'Adding…' : 'Add user'}</button>
      </div>
      <div style={{ marginTop: 8, fontSize: 13 }}>
        <span style={{ color: 'var(--mi-muted)' }}>Can see: </span>
        <ModuleAccess label="the new user" scope={scope} modules={modules} onChange={setScope} />
      </div>
      <p style={{ fontSize: 12, color: 'var(--mi-muted)', marginTop: 8, marginBottom: 0 }}>
        No email is sent — the new user's invite URL appears in the table above for you to share.
      </p>
    </form>
  );
}

const subHead: React.CSSProperties = { color: 'var(--mi-navy)', fontSize: 15, margin: '0 0 2px', display: 'flex', alignItems: 'center', gap: 8 };
const countPill: React.CSSProperties = { fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999, background: '#f1f3f5', color: 'var(--mi-muted)' };
const sectionHint: React.CSSProperties = { fontSize: 12, color: 'var(--mi-muted)', marginTop: 0, marginBottom: 8 };
const tabBar: React.CSSProperties = {
  display: 'flex', gap: 4, alignItems: 'center',
  borderBottom: '1px solid var(--mi-border)', marginBottom: 16,
};
const tabBtn: React.CSSProperties = {
  background: 'transparent', border: 'none', borderBottom: '2px solid transparent',
  padding: '8px 14px', fontSize: 14, color: 'var(--mi-muted)', cursor: 'pointer',
};
const tabActive: React.CSSProperties = {
  ...tabBtn, color: 'var(--mi-purple)', borderBottomColor: 'var(--mi-purple)', fontWeight: 600,
};
const inputBase: React.CSSProperties = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--mi-border)' };
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', background: 'var(--mi-surface)', border: '1px solid var(--mi-border)', borderRadius: 8, overflow: 'hidden' };
const thStyle: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', background: '#f1f3f5', borderBottom: '1px solid var(--mi-border)', fontSize: 13 };
const tdStyle: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid var(--mi-border)' };
